<template>
  <!-- prettier-ignore -->
  <div>
    <transition name="collapse">
      <div
        v-if="showNavigation"
        class="navigation"
        :class="{'navigation--collapsed': !showNavigation}">
        <template
          v-for="(item, index) in items"
          :key="item.name"
        >
          <span
            v-if="index > 0"
            :class="{ 'spacer': true, 'spacer--small': !!items[index].small }"
          />
          <div
            :class="getClasses(item)"
          >
            <router-link
              v-tooltip="item.tooltip ? $t(item.tooltip) : false"
              :cy-link="item.name"
              :to="(!item.disabled) ? item.path : ''"
            >
              <div class="navigation__bullet">
                <div v-text="item.text || index + 1" />
              </div>
              <div
                class="navigation__text"
                v-text="$t(getNavigationText(item.name))"
              />
            </router-link>
          </div>
        </template>
      </div>
    </transition>
  </div>
</template>

<script setup>
import { computed } from 'vue'
import { useRoute } from 'vue-router'
import { RouteHelper } from '@/helpers'

import { useBasket, useBrowser } from '@/hooks'

import { useI18n } from 'vue-i18n'

// HOOKS
const route = useRoute()
const { te } = useI18n()
const { browser } = useBrowser()
const { basket } = useBasket()

// COMPUTED
const items = computed(() => {
  // if offer already submitted => abort
  if (basket.submitted) return []

  return RouteHelper.getRoutes(route, basket).filter(__route => !__route.hidden && __route.isInNavigation)
})

const showNavigation = computed(() => {
  return route.meta?.navigation === undefined || route.meta?.navigation !== false
})

// METHODS
function getClasses(__route) {
  return {
    navigation__item: true,
    'navigation__item--active': !!__route.match,
    'navigation__item--complete': !!__route.complete,
    'navigation__item--disabled': !!__route.disabled,
    'navigation__item--invalid': !!__route.invalid && !__route.disabled,
    'navigation__item--small': !!__route.small,
  }
}

function getNavigationText(name) {
  const shortNameKey = `navigation.${name}-short`
  if (!browser.isDesktop && te(shortNameKey)) {
    return shortNameKey
  }

  return `navigation.${name}`
}
</script>

<style name="transitions" scoped>
.collapse-enter-active {
  animation: collapse-in 0.75s;
}
.collapse-leave-active {
  animation: collapse-in 0.75s reverse;
}

@keyframes collapse-in {
  0% {
    height: 0px;
  }
  100% {
    height: 60px;
  }
}

@media (--md) {
  @keyframes collapse-in {
    0% {
      height: 0px;
    }
    100% {
      height: 90px;
    }
  }
}

.navigation {
  a:before,
  a:after,
  .navigation__item,
  .navigation__bullet,
  .navigation__bullet div,
  .navigation__text {
    transition: all 0.5s ease-in-out;
  }
}
</style>

<style name="mobile" scoped>
#toggle {
  color: purple;
  position: absolute;
}

.navigation {
  width: 100%;
  height: 60px;
  user-select: none;

  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  padding: 0 10px;
  overflow-x: scroll;
  overflow-y: hidden;

  scrollbar-width: none;
  -ms-overflow-style: none;
  transition: height 0.5s ease-in-out;

  &::-webkit-scrollbar {
    width: 0;
    height: 0;
  }

  /*
  &--collapsed {
    height: 0 !important;
  }
  */

  a {
    display: flex;
    flex-direction: column;
    text-align: center;
    text-decoration: none;

    color: var(--c-primary-neutral-3);
    font-size: 12px;
    font-weight: normal;
    line-height: 15px;
    letter-spacing: -0.5px;
  }

  .navigation__item {
    min-width: 65px;

    transition: all 0.3s ease-in-out;

    .navigation__bullet {
      display: flex;
      flex-direction: column;
      justify-content: center;
      align-self: center;
      align-items: center;

      height: 30px;
      width: 30px;

      div {
        display: inline-block;
        text-align: center;
        height: 25px;
        width: 25px;
        line-height: 25px;

        border: 1px solid var(--c-primary-neutral-3);
        background-color: transparent;
      }
    }
  }

  .navigation__item--complete {
    .navigation__bullet div {
      background-color: var(--c-primary-color-2);
      border-color: var(--c-primary-color-2);
    }
  }

  .navigation__item--disabled,
  .navigation__item--disabled a {
    opacity: 0.5;
    cursor: not-allowed;
  }

  .navigation__item--active {
    .navigation__bullet div {
      height: 30px;
      width: 30px;
      line-height: 30px;
      border-radius: 50%;
      background-color: var(--c-primary-neutral-3);
      color: var(--c-primary-neutral-1);
      border-color: var(--c-primary-neutral-3);
    }
  }

  .navigation__item--small,
  .navigation__item--small a {
    .navigation__bullet div {
      height: 15px;
      width: 15px;
      border-radius: 50%;
      background-color: transparent;
      color: transparent !important;
      margin-top: -20px;
    }

    .navigation__text {
      display: none;
    }

    &.navigation__item--complete {
      .navigation__bullet div {
        background-color: var(--c-primary-color-2);
      }
    }

    &.navigation__item--active {
      .navigation__bullet div {
        height: 15px;
        width: 15px;
        color: transparent;
        background-color: var(--c-primary-neutral-3);
        border-color: var(--c-primary-neutral-3);
      }
    }

    & + .spacer {
      flex: 0 1 auto !important;
      width: 10px !important;
    }
  }

  .navigation__item--invalid,
  .navigation__item--invalid a {
    color: var(--c-secondary-color-1);

    .navigation__bullet div {
      border-color: var(--c-secondary-color-1);
      background-color: var(--c-secondary-color-1);
      color: var(--c-primary-neutral-3);
    }

    &.navigation__item--active {
      .navigation__bullet div {
        border-color: var(--c-secondary-color-1);
      }
    }
  }

  .spacer {
    flex: 1 1 auto;
    height: 100%;
    position: relative;
    transition: all 0.3s ease-in-out;

    &:after {
      content: '';
      position: absolute;
      height: 1px;
      top: 20px;

      left: -5px;
      right: -5px;

      background-color: var(--c-primary-neutral-3);
    }

    &--small {
      flex: 0 1 auto;
      width: 10px;
    }
  }
}
</style>

<style name="desktop" scoped>
@media (--md) {
  .navigation {
    max-width: 940px;
    margin: 0 auto;
    height: 90px;
    padding: 0 25px;

    a {
      font-size: 15px;
      line-height: 20px;
      letter-spacing: 0;
    }

    .navigation__item {
      min-width: 75px;
    }

    .spacer {
      top: 14px;

      &:after {
        left: -15px;
        right: -15px;
      }

      &--small {
        flex: 0 1 auto;
        width: 10px;

        &:after {
          right: -10px;
        }
      }
    }

    .navigation__item--small {
      min-width: 50px;

      & + .spacer {
        flex: 0 1 auto;
        width: 10px;

        &:after {
          left: -10px;
          right: -10px;
        }
      }
    }
  }
}
</style>
